// Ubicación en el repo: src/pages/HearingAids.jsx
// Listado de Audífonos (venta y calibración). Fase 1 (ver DECISIONS.md):
// venta + descuento de stock automático. Fase 2: estado de controles por
// fila + modal "Controles / calibraciones" (mismo criterio de badges que
// renderAudifonos() del prototipo, línea ~3070). Mismo patrón de tabla +
// búsqueda que Exams.jsx/Prescriptions.jsx, filtrando por la sucursal
// activa directo en `branch_id` (esta tabla, a diferencia de
// exams/prescriptions, no depende de patient_branches — ver hearingaids.js).
import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { listHearingaids, getHearingaidById, softDeleteHearingaid, hearingAidSummaryLine } from '../lib/hearingaids';
import { nextPendingControl } from '../lib/hearingaidControls';
import { listCatalog } from '../lib/catalog';
import { listActiveProfessionals } from '../lib/professionals';
import { isModuleEnabledForBranch } from '../lib/branches';
import { formatPatientName, formatDate, todayISO, addDaysISO } from '../lib/format';
import { getSignedCatalogImageUrl } from '../lib/catalogImages';
import { getBranchLogoUrl } from '../lib/branchLogos';
import { getProfessionalSignatureUrl } from '../lib/professionalSignatures';
import { printHTML } from '../lib/print';
import { hearingaidWarrantyHTML } from '../lib/receipts';
import HearingAidFormModal from '../components/HearingAidFormModal';
import HearingAidControlsModal from '../components/HearingAidControlsModal';
import QuotesList from '../components/QuotesList';
import Spinner from '../components/Spinner';

const INNER_TABS = [
  { key: 'ventas', label: 'Ventas' },
  { key: 'cotizaciones', label: 'Cotizaciones' },
];

function warrantyBadge(warrantyEnd) {
  if (!warrantyEnd) return null;
  const today = todayISO();
  const soon = addDaysISO(today, 30);
  if (warrantyEnd < today) {
    return <span className="badge badge-danger">Garantía vencida · {formatDate(warrantyEnd)}</span>;
  }
  if (warrantyEnd <= soon) {
    return <span className="badge badge-warn">Garantía próxima a vencer · {formatDate(warrantyEnd)}</span>;
  }
  return <span className="badge badge-neutral">Garantía hasta {formatDate(warrantyEnd)}</span>;
}

function controlStatusBadge(item) {
  const next = nextPendingControl(item.hearingaid_controls);
  if (!next) return <span className="badge badge-ok">Sin controles pendientes</span>;
  if (next.status === 'No se presentó') {
    return <span className="badge badge-danger">No se presentó · {formatDate(next.date)}</span>;
  }
  const today = todayISO();
  const soon = addDaysISO(today, 7);
  if (next.date < today) {
    return <span className="badge badge-danger">Control vencido · {formatDate(next.date)}</span>;
  }
  if (next.date <= soon) {
    return <span className="badge badge-warn">Control próximo · {formatDate(next.date)}</span>;
  }
  return <span className="badge badge-neutral">Próximo control · {formatDate(next.date)}</span>;
}

export default function HearingAids() {
  const { activeBranchId, activeBranch, professional } = useOutletContext();

  // Pestaña "Cotizaciones" (migración 0051, 26-ago-2026) — mismo patrón de
  // sub-pestañas internas que Catalog.jsx.
  const [innerTab, setInnerTab] = useState('ventas');

  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [controlsTarget, setControlsTarget] = useState(null);
  // Certificado de garantía (migración 0052, 16-sep-2026) — id de la fila
  // que está generando el documento en este momento, solo para deshabilitar
  // ese botón puntual mientras se resuelven la foto/logo (evita doble clic).
  const [printingWarrantyId, setPrintingWarrantyId] = useState(null);

  const refresh = useCallback(async () => {
    if (!activeBranchId) return;
    setLoading(true);
    setError('');
    try {
      setItems(await listHearingaids({ branchId: activeBranchId, search }));
    } catch (err) {
      setError(err.message || 'No se pudo cargar el listado de audífonos.');
    } finally {
      setLoading(false);
    }
  }, [activeBranchId, search]);

  useEffect(() => {
    const t = setTimeout(refresh, 300);
    return () => clearTimeout(t);
  }, [refresh]);

  useEffect(() => {
    if (!activeBranchId) return;
    // categorySpecialRole (migración 0050, 25-ago-2026) en vez de
    // `type: 'Audífono'` — ya no depende de que la categoría se llame
    // exactamente así, ver CategoryFormModal.jsx. branchId es lo que hace
    // que "en stock" (mostrado en el selector de modelo de
    // HearingAidFormModal) refleje la sucursal activa y no un número
    // global (Fase B, migración 0049) — también se recalcula si se cambia
    // de sucursal, no solo al abrir/cerrar el formulario.
    listCatalog({ categorySpecialRole: 'audifono', branchId: activeBranchId })
      .then(setCatalog)
      .catch(() => setCatalog([]));
  }, [formOpen, activeBranchId]);

  useEffect(() => {
    listActiveProfessionals().then(setProfessionals).catch(() => setProfessionals([]));
  }, []);

  function handleNew() {
    setEditTarget(null);
    setFormOpen(true);
  }

  async function handleEditClick(item) {
    setEditLoading(true);
    setError('');
    try {
      const full = await getHearingaidById(item.id);
      setEditTarget({ ...full, patients: item.patients });
      setFormOpen(true);
    } catch (err) {
      setError(err.message || 'No se pudo cargar la venta.');
    } finally {
      setEditLoading(false);
    }
  }

  async function handleDeleteClick(item) {
    const ok = window.confirm(
      `¿Eliminar este registro de audífono de ${formatPatientName(item.patients)}? El stock se restituirá.`
    );
    if (!ok) return;
    try {
      await softDeleteHearingaid(item, professional.id);
      await refresh();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el registro.');
    }
  }

  function handleFormSaved() {
    setFormOpen(false);
    setEditTarget(null);
    refresh();
  }

  // Certificado de garantía — usa lo que ya trae `item` desde listHearingaids
  // (HEARINGAID_SELECT ya embebe `catalog(image_path, warranty_terms)` y
  // `professionals!created_by(..., signature_path)`), solo falta resolver
  // las URLs firmadas (foto, logo, firma digital) justo antes de imprimir
  // (mismo criterio que QuotesList.jsx: nunca se guardan URLs firmadas, son
  // de corta duración). La firma es la del profesional que registró la
  // venta (`created_by`), no la de quien esté imprimiendo el documento
  // ahora — pedido explícito de Nicolás (16-sep-2026).
  async function handlePrintWarranty(item) {
    setPrintingWarrantyId(item.id);
    setError('');
    try {
      const [logoUrl, imageUrl, signatureUrl] = await Promise.all([
        getBranchLogoUrl(activeBranch),
        getSignedCatalogImageUrl(item.catalog?.image_path),
        getProfessionalSignatureUrl(item.professionals),
      ]);
      const html = hearingaidWarrantyHTML(item, imageUrl, activeBranch, logoUrl, signatureUrl);
      printHTML(`Garantia - ${formatPatientName(item.patients)}`, html);
    } catch (err) {
      setError(err.message || 'No se pudo generar el certificado de garantía.');
    } finally {
      setPrintingWarrantyId(null);
    }
  }

  // Guard de UX (DECISIONS.md #42) — ver mismo comentario en Exams.jsx.
  if (activeBranch && !isModuleEnabledForBranch(activeBranch, 'ORL')) {
    return (
      <div className="patients-page">
        <h1>Audífonos</h1>
        <p className="text-muted">El módulo ORL está deshabilitado para la sucursal activa ({activeBranch.name}).</p>
      </div>
    );
  }

  return (
    <div className="patients-page">
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {INNER_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={innerTab === t.key ? 'button-primary' : 'button-secondary'}
            onClick={() => setInnerTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {innerTab === 'cotizaciones' && (
        <QuotesList branchId={activeBranchId} activeBranch={activeBranch} catalog={catalog} actorId={professional.id} />
      )}

      {innerTab === 'ventas' && (
        <>
          <div className="patients-page__toolbar">
            <h1>Audífonos</h1>
            <div className="patients-page__toolbar-right">
              <input
                type="text"
                className="search-input"
                placeholder="Buscar por paciente o RUT…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button type="button" className="button-primary" onClick={handleNew}>
                + Nueva venta y calibración
              </button>
            </div>
          </div>

          {error && <p className="auth-error">{error}</p>}

          <table className="patients-table">
            <thead>
              <tr>
                <th>Paciente</th>
                <th>Producto</th>
                <th>Fecha de venta</th>
                <th>Garantía</th>
                <th>Controles</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6}><Spinner inline /></td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6}>No hay ventas de audífonos registradas.</td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div>{formatPatientName(item.patients)}</div>
                      <div className="text-muted">{item.patients?.rut || 'Sin RUT'}</div>
                    </td>
                    <td>{hearingAidSummaryLine(item)}</td>
                    <td>{formatDate(item.purchase_date)}</td>
                    <td>{warrantyBadge(item.warranty_end) || '—'}</td>
                    <td>{controlStatusBadge(item)}</td>
                    <td className="actions-cell">
                      <button type="button" className="link-button" onClick={() => setControlsTarget(item)}>
                        Controles
                      </button>
                      <button
                        type="button"
                        className="link-button"
                        disabled={printingWarrantyId === item.id}
                        onClick={() => handlePrintWarranty(item)}
                      >
                        🖨 {printingWarrantyId === item.id ? 'Generando…' : 'Imprimir garantía'}
                      </button>
                      <button type="button" className="link-button" disabled={editLoading} onClick={() => handleEditClick(item)}>
                        Editar
                      </button>
                      <button type="button" className="link-button link-button--danger" onClick={() => handleDeleteClick(item)}>
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </>
      )}

      {formOpen && (
        <HearingAidFormModal
          hearingaid={editTarget}
          branchId={activeBranchId}
          catalog={catalog}
          actorId={professional.id}
          onClose={() => {
            setFormOpen(false);
            setEditTarget(null);
          }}
          onSaved={handleFormSaved}
        />
      )}

      {controlsTarget && (
        <HearingAidControlsModal
          hearingaid={controlsTarget}
          branchId={activeBranchId}
          professionals={professionals}
          actorId={professional.id}
          onClose={() => setControlsTarget(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}
